import { useState } from 'react';
import { FormField, TextInput, TextArea, Select } from '@/components/ui';
import type { HubCatalogWebhook } from '@/shared/api/adminApi';

type Cfg = Record<string, unknown>;
type OnChange = (next: Cfg) => void;

const PLACEHOLDER_HINT = 'ניתן להזריק ערכים מהאירוע עם המשתנים הלחיצים למעלה';

/** רישום שדה להזרקת-משתנה: מחזיר ref+onFocus לשדה לפי מפתח config. */
export type FieldReg = (key: string) => {
  ref: (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  onFocus: () => void;
};

function str(cfg: Cfg, k: string): string {
  const v = cfg[k];
  return v == null ? '' : String(v);
}

/**
 * Config editor for a single action, keyed by action type.
 * When `fieldReg` is provided (automation builder), text fields register a
 * ref + onFocus so the clickable-variable bank can inject {{tokens}} at the
 * caret of whichever field was last focused.
 */
export function ActionConfigForm({
  type, config, onChange, fieldReg, webhooks,
}: {
  type: string;
  config: Cfg;
  onChange: OnChange;
  fieldReg?: FieldReg;
  /** Destinations for send_webhook; from hub/catalog. */
  webhooks?: HubCatalogWebhook[];
  // legacy no-op props (kept so callers passing them don't error)
  registerField?: (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  onFieldFocus?: () => void;
}) {
  const set = (k: string, v: unknown) => onChange({ ...config, [k]: v });
  const reg = (k: string) => (fieldReg ? fieldReg(k) : {});

  const Text = (k: string, label: string, opts?: { hint?: string; dir?: 'ltr' | 'rtl'; required?: boolean }) => (
    <FormField label={label} hint={opts?.hint} required={opts?.required}>
      <TextInput value={str(config, k)} onChange={(e) => set(k, e.target.value)} dir={opts?.dir} {...reg(k)} />
    </FormField>
  );
  const Area = (k: string, label: string, opts?: { hint?: string; required?: boolean }) => (
    <FormField label={label} hint={opts?.hint} required={opts?.required}>
      <TextArea value={str(config, k)} onChange={(e) => set(k, e.target.value)} rows={3} {...reg(k)} />
    </FormField>
  );

  switch (type) {
    case 'send_webhook': {
      // אפיון 12.4: בוחרים ובוק קיים ולא מקלידים כתובת חופשית. השרת דוחה
      // `url` ב-422, ולכן שדה חופשי כאן היה מסך שאי אפשר לשמור ממנו.
      const options = webhooks ?? [];
      const current = str(config, 'webhook_id');
      const legacyUrl = str(config, 'url');
      // ובוק שנבחר בעבר ומאז נמחק/הושבת — לומר זאת במפורש במקום להציג ריק.
      const missing = current !== '' && !options.some((w) => String(w.id) === current);

      return (
        <div className="stack">
          <FormField
            label="ובוק יעד"
            required
            hint={options.length === 0
              ? 'אין עדיין ובוקים. צרו אחד בלשונית "ובוקים" והוא יופיע כאן.'
              : 'הפעולה תישלח דרך הובוק הזה — עם הסוד, הכותרות והתבנית שלו, ועם רישום משלוח וניסיונות חוזרים.'}
          >
            <Select
              value={current}
              onChange={(v) => {
                const next: Cfg = { ...config, webhook_id: v ? Number(v) : '' };
                // כתובת חופשית ישנה נגררת אחרת בשקט ונדחית בשרת.
                delete next.url;
                delete next.secret;
                onChange(next);
              }}
              options={[
                { value: '', label: options.length ? '— בחרו ובוק —' : '— אין ובוקים —' },
                ...options.map((w) => ({
                  value: String(w.id),
                  label: w.active ? w.name : `${w.name} (מושבת)`,
                })),
              ]}
            />
          </FormField>

          {missing && (
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--warn)' }}>
              הובוק שנבחר אינו קיים עוד — יש לבחור אחר, אחרת הפעולה לא תישלח.
            </div>
          )}

          {legacyUrl !== '' && (
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--warn)' }}>
              הפעולה עדיין מוגדרת עם כתובת חופשית ({legacyUrl}). בחרו ובוק — שמירה
              תחליף אותה.
            </div>
          )}
        </div>
      );
    }
    case 'create_callback':
      return (
        <div className="stack">
          {Text('phone', 'טלפון', { hint: 'ריק = המספר מהאירוע', dir: 'ltr' })}
          {Text('name', 'שם')}
          <FormField label="מועד השיחה החוזרת (דקות מעכשיו)" hint="ברירת מחדל 60 — מתי הנציג יתקשר חזרה (שונה מהשהיית ההרצה למטה).">
            <TextInput
              type="number"
              value={config.delay_minutes == null ? '' : String(config.delay_minutes)}
              onChange={(e) => set('delay_minutes', e.target.value === '' ? '' : Number(e.target.value))}
              dir="ltr"
            />
          </FormField>
          {Text('reason', 'סיבה', { hint: PLACEHOLDER_HINT })}
          {Text('agent_id', 'מזהה נציג', { dir: 'ltr' })}
        </div>
      );
    case 'update_contact':
      return (
        <div className="stack">
          {Text('phone', 'טלפון', { hint: 'ריק = מהאירוע', dir: 'ltr' })}
          {Text('add_tag', 'הוסף תגית')}
          {Text('status', 'סטטוס')}
          {Text('owner_id', 'שיוך לבעלים (מזהה נציג)', { dir: 'ltr' })}
          {Area('note', 'הערה', { hint: PLACEHOLDER_HINT })}
        </div>
      );
    case 'fireberry_upsert_contact':
      return (
        <div className="stack">
          {Text('phone', 'טלפון', { hint: 'ריק = הטלפון של הלקוח מהאירוע', dir: 'ltr' })}
          {Text('name', 'שם', { hint: 'ריק = השם מהאירוע. "שם משפחה" נגזר אחרי הרווח הראשון.' })}
          {Text('email', 'אימייל', { dir: 'ltr' })}
          <FormField label="אובייקט בפיירברי" hint="ריק = ברירת המחדל מהגדרות → חברה">
            <Select
              value={str(config, 'object')}
              onChange={(v) => set('object', v)}
              options={[
                { value: '', label: 'ברירת מחדל' },
                { value: 'contact', label: 'איש קשר (contact)' },
                { value: 'account', label: 'לקוח / חשבון (account)' },
              ]}
            />
          </FormField>
          <FieldMap config={config} set={set} label="שדות נוספים בפיירברי" hint="שם השדה כפי שהוא בפיירברי (למשל description, statuscode) → ערך. אפשר משתנים." />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
            <input type="checkbox" checked={config.update_existing !== false} onChange={(e) => set('update_existing', e.target.checked)} />
            לעדכן גם רשומה קיימת (בלי סימון: רק יצירה כשחסר)
          </label>
        </div>
      );
    case 'fireberry_log_call':
      return (
        <div className="stack">
          {Text('phone', 'טלפון הלקוח', { hint: 'ריק = לפי כיוון השיחה מהאירוע', dir: 'ltr' })}
          <CreateMissing config={config} set={set} />
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            נרשם אוטומטית: כיוון (נכנסת/יוצאת), סטטוס (נענתה/תפוס/לא נענתה), משך, מספרים, קישור להקלטה ומזהה השיחה.
          </div>
        </div>
      );
    case 'fireberry_add_note':
      return (
        <div className="stack">
          {Text('phone', 'טלפון הלקוח', { hint: 'ריק = מהאירוע', dir: 'ltr' })}
          {Area('text', 'טקסט ההערה', { hint: 'ריק = קוד סיום, הערות הנציג וסיכום מהאירוע. ' + PLACEHOLDER_HINT })}
          <CreateMissing config={config} set={set} />
        </div>
      );
    case 'fireberry_update_record':
      return (
        <div className="stack">
          {Text('phone', 'טלפון הלקוח', { hint: 'ריק = מהאירוע', dir: 'ltr' })}
          <FieldMap config={config} set={set} label="שדות לעדכון" hint="שם השדה בפיירברי → ערך. למשל statuscode → 2, או {{data.attempts}} עם תרגום ערכים למטה." />
          <ValueMapEditor config={config} set={set} />
          <CreateMissing config={config} set={set} />
        </div>
      );
    case 'create_ticket':
      return (
        <div className="stack">
          {Text('subject', 'נושא', { required: true, hint: PLACEHOLDER_HINT })}
          {Area('body', 'תוכן', { hint: PLACEHOLDER_HINT })}
          <FormField label="עדיפות">
            <Select
              value={str(config, 'priority') || 'normal'}
              onChange={(v) => set('priority', v)}
              options={[
                { value: 'low', label: 'נמוכה' },
                { value: 'normal', label: 'רגילה' },
                { value: 'high', label: 'גבוהה' },
                { value: 'urgent', label: 'דחופה' },
              ]}
            />
          </FormField>
        </div>
      );
    case 'send_whatsapp':
      return (
        <div className="stack">
          {Text('to', 'נמען', { hint: 'ריק = המספר מהאירוע', dir: 'ltr' })}
          {Area('message', 'הודעה', { hint: PLACEHOLDER_HINT, required: true })}
        </div>
      );
    case 'send_email':
      return (
        <div className="stack">
          {Text('to', 'נמען', { dir: 'ltr', required: true, hint: PLACEHOLDER_HINT })}
          {Text('subject', 'נושא', { required: true, hint: PLACEHOLDER_HINT })}
          {Area('body', 'תוכן', { hint: PLACEHOLDER_HINT })}
        </div>
      );
    default:
      return <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין הגדרות לסוג פעולה זה.</div>;
  }
}


/** "צור אם חסר" — משותף לפעולות פיירברי שמקשרות לרשומה קיימת. */
function CreateMissing({ config, set }: { config: Cfg; set: (k: string, v: unknown) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
      <input type="checkbox" checked={config.create_missing === true} onChange={(e) => set('create_missing', e.target.checked)} />
      אם הלקוח לא קיים בפיירברי — ליצור אותו (לפי טלפון ושם מהאירוע)
    </label>
  );
}

/**
 * עורך map של שדה→ערך (config.fields). נשמר כאובייקט; שורות ריקות לא נשמרות.
 * הערכים עוברים templating בשרת ({{data.x}}), ולכן הם טקסט חופשי.
 */
function FieldMap({ config, set, label, hint }: { config: Cfg; set: (k: string, v: unknown) => void; label: string; hint?: string }) {
  const map = (config.fields && typeof config.fields === 'object' ? config.fields : {}) as Record<string, unknown>;
  const rows = Object.entries(map);
  const write = (next: [string, unknown][]) => {
    const obj: Record<string, unknown> = {};
    for (const [k, v] of next) { if (k.trim()) obj[k.trim()] = v; }
    set('fields', obj);
  };
  return (
    <FormField label={label} hint={hint}>
      <div className="stack" style={{ gap: 6 }}>
        {rows.map(([k, v], i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
            <TextInput value={k} dir="ltr" placeholder="שדה בפיירברי" aria-label="שם שדה"
              onChange={(e) => { const next = [...rows] as [string, unknown][]; next[i] = [e.target.value, v]; write(next); }} />
            <TextInput value={v == null ? '' : String(v)} placeholder="ערך / {{data.x}}" aria-label="ערך שדה"
              onChange={(e) => { const next = [...rows] as [string, unknown][]; next[i] = [k, e.target.value]; write(next); }} />
            <button type="button" className="btn ghost" aria-label="הסר שדה" onClick={() => write(rows.filter((_, j) => j !== i) as [string, unknown][])}>✕</button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={() => write([...rows, [`field${rows.length + 1}`, '']] as [string, unknown][])}>+ הוסף שדה</button>
      </div>
    </FormField>
  );
}


/**
 * תרגום ערכים פר-שדה (config.value_map): אחרי שהערך מרונדר מהאירוע, הוא
 * מתורגם לערך של פיירברי. למשל pcfsystemfield102: {"1":"7","2":"8","*":"10"}
 * הופך "מספר ניסיון" ל"אין מענה 1..4". '*' = ברירת מחדל.
 */
function ValueMapEditor({ config, set }: { config: Cfg; set: (k: string, v: unknown) => void }) {
  const vm = (config.value_map && typeof config.value_map === 'object' ? config.value_map : {}) as Record<string, Record<string, unknown>>;
  const fields = Object.keys((config.fields && typeof config.fields === 'object' ? config.fields : {}) as Record<string, unknown>);
  const [text, setText] = useState(() => (Object.keys(vm).length ? JSON.stringify(vm, null, 2) : ''));
  const [bad, setBad] = useState(false);
  if (fields.length === 0) return null;
  return (
    <FormField
      label="תרגום ערכים (אופציונלי)"
      hint={`JSON: { "${fields[0]}": { "ערך מהאירוע": "ערך בפיירברי", "*": "ברירת מחדל" } }`}
    >
      <TextArea
        value={text}
        rows={4}
        dir="ltr"
        aria-label="תרגום ערכים"
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) { setBad(false); set('value_map', {}); return; }
          try { const parsed = JSON.parse(e.target.value); setBad(false); set('value_map', parsed); }
          catch { setBad(true); }
        }}
      />
      {bad && <div style={{ color: 'var(--danger)', fontSize: 'var(--t-xs)' }}>JSON לא תקין — לא נשמר עד שיתוקן</div>}
    </FormField>
  );
}
